import { useState, useEffect, useMemo } from "react";
import DetailedDirectCostVm from "./detailedDirectCostVm";
import VmMonthlyCost from "./vmMontlyCost";
import SharedCostsAndRules from "./SharedCostsAndRules";
import VmCostHistoryTable from "./VmCostHistoryTable";
import { useVmCostByMonth } from "../../../hooks/useVmCostsByMonth";
import { FaCalendarAlt } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AIVmCostAnalysisCard from "./AIVmCostAnalysisCard";

const VmFinanceComponent = ({ vmIds, vmNames }) => {
  const {
    getVmCostDetail,
    getVmCostsByServiceForVm,
    checkMonthHasData,
    loading: apiLoading,
  } = useVmCostByMonth();
  const [selectedDate, setSelectedDate] = useState(null);
  const [allVmData, setAllVmData] = useState({});
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);

  const vmIdList = vmIds && Array.isArray(vmIds) ? vmIds : vmIds ? [vmIds] : [];
  const vmNameList =
    vmNames && Array.isArray(vmNames) ? vmNames : vmNames ? [vmNames] : [];

  useEffect(() => {
    const initializeMonth = async () => {
      setInitializing(true);

      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth();

      let targetDate = new Date(currentYear, currentMonth - 1, 1);

      let hasData = await checkMonthHasData(
        vmIdList,
        targetDate.getFullYear(),
        targetDate.getMonth() + 1,
      );
      let fallbackDate = targetDate;

      if (!hasData) {
        for (let i = 0; i < 6; i++) {
          const checkDate = new Date(
            targetDate.getFullYear(),
            targetDate.getMonth() - i,
            1,
          );
          if (checkDate.getFullYear() < 2024) break;

          const dataExists = await checkMonthHasData(
            vmIdList,
            checkDate.getFullYear(),
            checkDate.getMonth() + 1,
          );
          if (dataExists) {
            fallbackDate = checkDate;
            break;
          }
        }
      }

      setSelectedDate(fallbackDate);
      setInitializing(false);
    };

    if (vmIdList.length > 0) {
      initializeMonth();
    }
  }, [vmIdList]);

  useEffect(() => {
    if (selectedDate && vmIdList.length && !initializing) {
      fetchAllVmCosts();
    }
  }, [selectedDate, vmIdList, initializing]);

  const fetchAllVmCosts = async () => {
    setLoading(true);
    const results = {};
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth() + 1;

    for (const vmId of vmIdList) {
      const detailResult = await getVmCostDetail(vmId, year, month);
      const serviceResult = await getVmCostsByServiceForVm(vmId, year, month);

      results[vmId] = {
        costDetail: detailResult.success ? detailResult.data : null,
        serviceCosts: serviceResult.success ? serviceResult.data : [],
      };
    }

    setAllVmData(results);
    setLoading(false);
  };

  const handleDateChange = async (date) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;

    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;

    if (year > currentYear || (year === currentYear && month > currentMonth)) {
      alert(
        `Cannot select future months. Please select ${currentYear}-${String(currentMonth).padStart(2, "0")} or earlier.`,
      );
      return;
    }

    const hasData = await checkMonthHasData(vmIdList, year, month);

    if (hasData) {
      setSelectedDate(date);
    } else {
      alert(
        `No cost data available for ${year}-${String(month).padStart(2, "0")}. Please select another month.`,
      );
    }
  };

  const formatMonthDisplay = () => {
    if (!selectedDate) return "Select month";
    return selectedDate.toLocaleString("default", {
      month: "long",
      year: "numeric",
    });
  };

  const globalTotal = useMemo(() => {
    return Object.values(allVmData).reduce((sum, vm) => {
      return sum + (vm.costDetail?.totalCost || 0);
    }, 0);
  }, [allVmData]);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  if (!vmIdList.length) {
    return (
      <div className="bg-primary-dark rounded-2xl p-6 border border-gray-700/50">
        <div className="text-center py-8">
          <p className="text-gray-400">No VM selected</p>
        </div>
      </div>
    );
  }

  if (initializing || !selectedDate) {
    return (
      <div className="bg-primary-dark rounded-2xl p-6 border border-gray-700/50">
        <div className="text-center py-8">
          <div className="inline-block w-8 h-8 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 mt-3">Loading available months...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-start ml-4">
        <div className="relative">
          <DatePicker
            selected={selectedDate}
            onChange={handleDateChange}
            dateFormat="MMMM yyyy"
            showMonthYearPicker
            maxDate={new Date()}
            minDate={new Date(2024, 0, 1)}
            customInput={
              <button className="flex items-center gap-2 bg-primary-cyan hover:bg-primary-cyan/90 text-white px-6 py-2 rounded-lg transition-all duration-200 font-medium text-base">
                <FaCalendarAlt size={20} />
                <span>{formatMonthDisplay()}</span>
              </button>
            }
            popperPlacement="bottom-end"
            popperClassName="z-50"
            calendarClassName="bg-primary-dark border border-gray-700 rounded-lg shadow-xl"
            wrapperClassName="w-full"
          />
        </div>
      </div>

      {vmIdList.map((vmId, index) => {
        const vmData = allVmData[vmId];

        return (
          <div
            key={vmId}
            className="bg-primary-dark rounded-2xl p-6 border border-gray-700/50 mx-3"
          >
            <div className="flex items-center gap-2 mb-6 pb-3 border-b border-gray-700">
              <div className="w-2 h-2 rounded-full bg-primary-cyan"></div>
              <h3 className="text-lg font-semibold text-white">
                {vmNameList[index] || `VM ${vmId}`}
              </h3>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <DetailedDirectCostVm
                data={vmData?.serviceCosts || []}
                loading={loading && !vmData}
              />
              <VmMonthlyCost
                directCost={vmData?.costDetail?.directCost}
                reservationCost={vmData?.costDetail?.reservationCost}
                sharedCost={vmData?.costDetail?.sharedCost}
                loading={loading && !vmData}
              />
            </div>
            <AIVmCostAnalysisCard vmId={vmId} vmName={vmNameList[index]} />
          </div>
        );
      })}

      <div className="bg-primary-dark mx-3 rounded-xl p-4 border border-gray-700">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-primary-cyan"></div>
            <span className="text-gray-400 font-medium">TOTAL COST</span>
          </div>
          <span className="text-primary-cyan text-2xl font-bold">
            {formatAmount(globalTotal)}
          </span>
        </div>
      </div>

      <SharedCostsAndRules
        selectedYear={selectedDate.getFullYear()}
        selectedMonth={selectedDate.getMonth() + 1}
      />

      <VmCostHistoryTable vmIds={vmIdList} vmNames={vmNameList} />
    </div>
  );
};

export default VmFinanceComponent;
